/**
 * The /docs catalogue model (pure): the API response indexed and grouped
 * for the page — themes and sources in first-occurrence order of the API's
 * sort, the counts on the Pregled tiles, the WEB THEME PAGES with their
 * page-list nav and its diacritic-insensitive search, and, since 0584, the
 * KingCAD side: the desktop tree as a flat pre-order list, the FOLDER PAGES
 * read off it, the collapsible tree nav, and tree-order neighbours.
 *
 * Two sections, one response. `entries` holds the entries the WEB MAP serves
 * only, so every consumer written before the KingCAD section (themes, Pregled
 * counts) stays web-only untouched; `cadEntries` holds the KingCAD layer
 * entries; `byKey` and `all` span both.
 *
 * A product entry the web map does not serve (`surfaceOf` = `desktop`: bound
 * to KingCAD maps or a DOF / LiDAR catalog, no web toggle, no served tiles —
 * Teo 2026-09-16: "only layers in splet are the ones available on the web")
 * leaves the web list for `desktopOnly` and reads under the CAD tab: reachable
 * from its KingCAD layers' »Podatkovni sloj« link, from the »Rastri in LiDAR«
 * folder when it is a catalog product, and by URL. `entrySection` says which
 * tab a key belongs to.
 *
 * WEB entries have NO page of their own since 2026-09-28 (Teo: "1 group is 1
 * page, with listed entries for each layer … same ui/ux for web as for cad
 * page"): every theme is one page (`WebPage`, `/docs/web/<slug>`) and its
 * entries are sections on it (`#sloj-<key>`), in API order — a parent before
 * its children of the same theme; a child whose parent sits in another theme
 * (the GJI varovalni pasovi, the presets) reads on ITS theme's page with the
 * parent line linking across. The theme slug is the folded theme name.
 *
 * KingCAD layers have NO page of their own either (Teo 2026-09-16: "each lowest
 * level folder [has] a page, with layers all on 1 page listed"): every folder
 * that holds layers is one page (`CadPage`, `…/docs/cad/<slugs…>`) and its
 * layers are sections on it (`#sloj-<xml_id>`). A folder that holds layers AND
 * sub-folders is a page whose sub-folder pages it links to; a folder with only
 * sub-folders is a nav container, not a page. The »Rastri in LiDAR« folder is
 * the one exception: its rows are product entries with their own pages — as is
 * every product only KingCAD serves.
 *
 * ONE HALF per catalogue since 2026-09-28 (Teo: "/si/docs and /cz/docs … each holds
 * its own"): `buildCatalogue(res, country)` keeps the entries of that country and
 * that country's desktop tree (`res.cad[country]`), so themes, sources, pages and
 * counts are per half and no link ever crosses. The section landings are card grids
 * (`webLandingCards` / `cadLandingCards`); a KingCAD folder name splits at its first
 * comma into the label and the publisher (`splitFolderLabel`).
 */

import type {
  WikiCadFolderNode,
  WikiCadLayerNode,
  WikiCadNode,
  WikiCadTree,
  WikiCountry,
  WikiEntry,
  WikiEnv,
  WikiLayersResponse,
  WikiSource,
} from '../../shared/types/layerWiki.js';
import { docsCopy } from './docsCopy.js';
import {
  cadFolderPath,
  cadLayerAnchorId,
  entryPath,
  webEntryAnchorId,
  webPagePath as webPagePathOf,
} from './docsRouter.js';
import type { DocsSection } from './docsSection.js';
import { surfaceOf } from './wikiSurface.js';

export interface DocsCatalogue {
  env: WikiEnv;
  generatedAt: string;
  staleDays: number;
  /** The /docs half this catalogue is: only entries of this country (2026-09-28). */
  country: WikiCountry;
  /** Product entries the web map serves, API order (sort_order, key). */
  entries: WikiEntry[];
  /** Product entries only KingCAD serves (no web surface), API order. */
  desktopOnly: WikiEntry[];
  /** Catalog products (DOF, LiDAR) of both product lists, API order — the »Rastri in LiDAR« rows. */
  rastri: WikiEntry[];
  /** KingCAD layer entries in API order. */
  cadEntries: WikiEntry[];
  /** Every entry of both sections, API order. */
  all: WikiEntry[];
  /** Indexed over `all`. */
  byKey: Map<string, WikiEntry>;
  /** Which tab each key of `all` belongs to: `web`, or `cad` for CAD layers AND desktop-only products. */
  sectionByKey: Map<string, DocsSection>;
  /** Children by `parentKey`, in API order. */
  childrenByKey: Map<string, WikiEntry[]>;
  /** `entry.theme` values of the web entries in first-occurrence order. */
  themes: string[];
  /** Publishers of every entry in first-occurrence order of `entry.source.id`. */
  sources: WikiSource[];
  sourceById: Map<string, WikiSource>;
  /** The web theme pages in theme order (2026-09-28). */
  webPages: WebPage[];
  webPageBySlug: Map<string, WebPage>;
  /** The theme page listing each web entry. */
  webPageByKey: Map<string, WebPage>;
  /** This half's desktop tree (`res.cad[country]`), null when the server has none. */
  cadTree: WikiCadTree | null;
  /** Where each CAD entry (and each »Rastri in LiDAR« product) sits in the tree. */
  cadIndex: Map<string, CadPlace>;
  /** Every folder node of `cadFlatRows` by id (the synthetic »Rastri in LiDAR« folder included). */
  cadFolderById: Map<string, WikiCadFolderNode>;
  /** The folder pages in tree order. */
  cadPages: CadPage[];
  cadPageById: Map<string, CadPage>;
  /** Pages by their URL path (`slugs.join('/')`). */
  cadPageByPath: Map<string, CadPage>;
  counts: {
    layers: number;
    cadLayers: number;
    /** Depth-0 folders of the tree, the »Rastri in LiDAR« folder included. */
    cadFolders: number;
    /** Catalog products listed under »Rastri in LiDAR« (web entries, counted once more here). */
    cadRastri: number;
    sources: number;
    themes: number;
    /** Entries whose licence text differs from their source's (an entry-level override). */
    ownLicence: number;
    static: number;
  };
}

/** One web theme page (Teo 2026-09-28): a theme of the web catalogue, its entries as sections. */
export interface WebPage {
  /** The URL slug (the folded theme name), unique among the pages. */
  slug: string;
  theme: string;
  /** The theme's web entries, API order (a parent before its children of the same theme). */
  entries: WikiEntry[];
  /** 0-based position among the pages (prev/next). */
  position: number;
}

export interface CadPlace {
  /** Folder names from the include folder down to the layer's folder. */
  path: string[];
  /** The same folders' ids (the nav opens them). */
  folderIds: string[];
  /** 0-based position among the tree's layer rows. */
  position: number;
  /** The folder page listing the layer; null for a »Rastri in LiDAR« row (a product entry). */
  pageId: string | null;
  /** The layer's section anchor on that page; null for a »Rastri in LiDAR« row. */
  anchor: string | null;
}

/** One folder page: a folder of the tree that holds layers (Teo 2026-09-16). */
export interface CadPage {
  /** The folder's node id (`f1-1`). */
  id: string;
  folder: WikiCadFolderNode;
  /** URL segments: the slugs from the include folder down to this folder. */
  slugs: string[];
  /** Folder names from the include folder down, this folder's last. */
  names: string[];
  /** Folder ids from the include folder down, this folder's last. */
  folderIds: string[];
  /** The folder's own layers, tree order. */
  layers: WikiCadLayerNode[];
  /** Ids of the nearest folder pages below this one (a folder holding layers AND sub-folders), tree order. */
  subPages: string[];
  /** 0-based position among the pages (prev/next). */
  position: number;
}

/** The synthetic trailing folder for DOF / LiDAR / relief products (no sxmaps folder holds them). */
export const RASTRI_FOLDER_ID = 'rastri';

/** A catalog product (DOF sheets, LiDAR tiles): KingCAD reads it outside the layer tree. */
export function isCatalogProduct(e: WikiEntry): boolean {
  return e.desktop.catalogs.length > 0;
}

/**
 * The tree the KingCAD nav renders: the server's nodes plus the »Rastri in LiDAR«
 * folder with one row per catalog product (web entries with a DOF / LiDAR catalog).
 */
export function cadFlatRows(cat: DocsCatalogue): WikiCadNode[] {
  const rows: WikiCadNode[] = cat.cadTree ? [...cat.cadTree.nodes] : [];
  const rastri = cat.rastri;
  if (rastri.length > 0) {
    rows.push({
      kind: 'folder',
      id: RASTRI_FOLDER_ID,
      slug: RASTRI_FOLDER_ID,
      depth: 0,
      name: docsCopy.cad.rastriFolder,
      desc: null,
      sourceId: null,
      sourceName: null,
    });
    for (const e of rastri)
      rows.push({
        kind: 'layer',
        id: `rastri:${e.key}`,
        depth: 1,
        xmlId: '',
        name: navLabel(e),
        entryKey: e.key,
      });
  }
  return rows;
}

/**
 * The folder pages: one per folder that holds layers, in tree order. A layer's
 * folder is the stack entry one depth up (pre-order: a folder's own layers come
 * right after it, before its sub-folders). The »Rastri in LiDAR« rows are
 * product entries and make no page.
 */
function buildCadPages(rows: readonly WikiCadNode[]): CadPage[] {
  const pages: CadPage[] = [];
  const byId = new Map<string, CadPage>();
  const stack: WikiCadFolderNode[] = [];
  for (const n of rows) {
    if (n.kind === 'folder') {
      stack.length = n.depth;
      stack.push(n);
      continue;
    }
    const chain = stack.slice(0, n.depth);
    const parent = chain[chain.length - 1];
    if (!parent || parent.id === RASTRI_FOLDER_ID) continue; // a root-level layer (none today) or a rastri row
    let page = byId.get(parent.id);
    if (!page) {
      page = {
        id: parent.id,
        folder: parent,
        slugs: chain.map((f) => f.slug),
        names: chain.map((f) => f.name),
        folderIds: chain.map((f) => f.id),
        layers: [],
        subPages: [],
        position: pages.length,
      };
      pages.push(page);
      byId.set(parent.id, page);
      // The nearest page above links down to this one (»Podmape«).
      for (let i = chain.length - 2; i >= 0; i -= 1) {
        const above = byId.get((chain[i] as WikiCadFolderNode).id);
        if (above) {
          above.subPages.push(page.id);
          break;
        }
      }
    }
    page.layers.push(n);
  }
  return pages;
}

function indexCad(
  rows: readonly WikiCadNode[],
  pageById: ReadonlyMap<string, CadPage>
): Map<string, CadPlace> {
  const index = new Map<string, CadPlace>();
  const names: string[] = [];
  const ids: string[] = [];
  let position = 0;
  for (const n of rows) {
    if (n.kind === 'folder') {
      names.length = n.depth;
      ids.length = n.depth;
      names.push(n.name);
      ids.push(n.id);
      continue;
    }
    if (n.entryKey) {
      const folderId = ids[n.depth - 1];
      const page = folderId ? (pageById.get(folderId) ?? null) : null;
      index.set(n.entryKey, {
        path: names.slice(0, n.depth),
        folderIds: ids.slice(0, n.depth),
        position,
        pageId: page?.id ?? null,
        anchor: page ? cadLayerAnchorId(n.xmlId) : null,
      });
    }
    position += 1;
  }
  return index;
}

/**
 * A theme's URL slug: the folded name (č/š/ž → c/s/z, lowercase), runs of anything
 * else collapsed to one dash — »Kataster nepremičnin« → `kataster-nepremicnin`,
 * »ČÚZK – RÚIAN« → `cuzk-ruian`.
 */
export function slugifyTheme(theme: string): string {
  const s = fold(theme)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s === '' ? 'tema' : s;
}

/** The web theme pages: one per theme in theme order; a clashing slug gets a `-2`, `-3` … suffix. */
function buildWebPages(entries: readonly WikiEntry[], themes: readonly string[]): WebPage[] {
  const pages: WebPage[] = [];
  const taken = new Set<string>();
  for (const theme of themes) {
    const base = slugifyTheme(theme);
    let slug = base;
    for (let i = 2; taken.has(slug); i += 1) slug = `${base}-${i}`;
    taken.add(slug);
    pages.push({
      slug,
      theme,
      entries: entries.filter((e) => e.theme === theme),
      position: pages.length,
    });
  }
  return pages;
}

/**
 * The catalogue of ONE half (2026-09-28): only the entries whose `country` is the page's
 * (a pre-0616 server sends none — those read as si) and that half's desktop tree.
 */
export function buildCatalogue(res: WikiLayersResponse, country: WikiCountry): DocsCatalogue {
  const all = res.entries.filter((e) => (e.country ?? 'si') === country);
  const entries: WikiEntry[] = [];
  const desktopOnly: WikiEntry[] = [];
  const rastri: WikiEntry[] = [];
  const cadEntries: WikiEntry[] = [];
  const byKey = new Map<string, WikiEntry>();
  const sectionByKey = new Map<string, DocsSection>();
  const childrenByKey = new Map<string, WikiEntry[]>();
  const themes: string[] = [];
  const sources: WikiSource[] = [];
  const seenSource = new Set<string>();
  let ownLicence = 0;
  let staticCount = 0;
  // Index first: a child's surface is read off its parent's web bindings.
  for (const e of all) byKey.set(e.key, e);
  for (const e of all) {
    if (!seenSource.has(e.source.id)) {
      seenSource.add(e.source.id);
      sources.push(e.source);
    }
    // A pre-0584 server sends no `section`; everything is web then.
    if (e.section === 'cad') {
      cadEntries.push(e);
      sectionByKey.set(e.key, 'cad');
      continue;
    }
    // Licence and static counts describe the products (Viri spans both tabs).
    if (e.licence !== null && e.licence !== e.source.licence) ownLicence += 1;
    if (e.status === 'static') staticCount += 1;
    if (isCatalogProduct(e)) rastri.push(e);
    const parent = e.parentKey ? byKey.get(e.parentKey) : null;
    if (surfaceOf(e, parent) === 'desktop') {
      desktopOnly.push(e);
      sectionByKey.set(e.key, 'cad');
      continue;
    }
    entries.push(e);
    sectionByKey.set(e.key, 'web');
    if (e.parentKey) {
      const list = childrenByKey.get(e.parentKey) ?? [];
      list.push(e);
      childrenByKey.set(e.parentKey, list);
    }
    if (!themes.includes(e.theme)) themes.push(e.theme);
  }
  const cadTree = res.cad?.[country] ?? null;
  const cat: DocsCatalogue = {
    env: res.env,
    generatedAt: res.generatedAt,
    staleDays: res.staleDays,
    country,
    entries,
    desktopOnly,
    rastri,
    cadEntries,
    all,
    byKey,
    sectionByKey,
    childrenByKey,
    themes,
    sources,
    sourceById: new Map(sources.map((s) => [s.id, s])),
    webPages: [],
    webPageBySlug: new Map(),
    webPageByKey: new Map(),
    cadTree,
    cadIndex: new Map(),
    cadFolderById: new Map(),
    cadPages: [],
    cadPageById: new Map(),
    cadPageByPath: new Map(),
    counts: {
      layers: entries.length,
      cadLayers: cadEntries.length,
      cadFolders: 0,
      cadRastri: rastri.length,
      sources: sources.length,
      themes: themes.length,
      ownLicence,
      static: staticCount,
    },
  };
  cat.webPages = buildWebPages(entries, themes);
  for (const p of cat.webPages) {
    cat.webPageBySlug.set(p.slug, p);
    for (const e of p.entries) cat.webPageByKey.set(e.key, p);
  }
  const rows = cadFlatRows(cat);
  for (const n of rows) if (n.kind === 'folder') cat.cadFolderById.set(n.id, n);
  cat.cadPages = buildCadPages(rows);
  for (const p of cat.cadPages) {
    cat.cadPageById.set(p.id, p);
    cat.cadPageByPath.set(p.slugs.join('/'), p);
  }
  cat.cadIndex = indexCad(rows, cat.cadPageById);
  cat.counts.cadFolders = rows.filter((n) => n.kind === 'folder' && n.depth === 0).length;
  return cat;
}

export function parentOf(cat: DocsCatalogue, e: WikiEntry): WikiEntry | null {
  return e.parentKey ? (cat.byKey.get(e.parentKey) ?? null) : null;
}

/** The tab a key reads under: `cad` for a CAD layer or a desktop-only product, else `web` (unknown keys too). */
export function entrySection(cat: DocsCatalogue, key: string): DocsSection {
  return cat.sectionByKey.get(key) ?? 'web';
}

/** The entries whose `parentKey` is this one, in API order. */
export function childrenOf(cat: DocsCatalogue, key: string): WikiEntry[] {
  return cat.childrenByKey.get(key) ?? [];
}

/** Diacritic-insensitive, Slovenian-lowercased haystack/needle form (č/š/ž → c/s/z). */
export function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('sl');
}

/** The nav row's label: the short form when the seed has one, else the title. */
export function navLabel(e: WikiEntry): string {
  return e.shortLabel ?? e.title;
}

/** True when the folded needle occurs in the entry's title, Czech title, short label, key, theme or source. */
export function matchesQuery(e: WikiEntry, needle: string): boolean {
  const nq = fold(needle.trim());
  if (!nq) return true;
  const hay = fold(
    [
      e.title,
      e.titleCs ?? '',
      e.shortLabel ?? '',
      e.key,
      e.theme,
      e.source.name,
      e.source.short,
    ].join(' ')
  );
  return hay.includes(nq);
}

/** Previous / next WEB entry in the API order (unfiltered) — the entry page's pager fallback. */
export function neighbours(
  cat: DocsCatalogue,
  key: string
): { prev: WikiEntry | null; next: WikiEntry | null } {
  const idx = cat.entries.findIndex((e) => e.key === key);
  if (idx < 0) return { prev: null, next: null };
  return { prev: cat.entries[idx - 1] ?? null, next: cat.entries[idx + 1] ?? null };
}

// ---------------------------------------------------------------------------
// Web theme pages + page-list nav (2026-09-28)
// ---------------------------------------------------------------------------

/** The page at `/docs/web/<slug>`, or null. */
export function webPageForSlug(cat: DocsCatalogue, slug: string): WebPage | null {
  return cat.webPageBySlug.get(slug) ?? null;
}

/** `/docs/web/<slug>` of a page. */
export function webPagePath(page: WebPage): string {
  return webPagePathOf(page.slug);
}

/** The theme page + anchor of a WEB entry, or null when it is not a web entry (a CAD layer, a desktop-only product, an unknown key). */
export function webEntryHref(cat: DocsCatalogue, key: string): string | null {
  const page = cat.webPageByKey.get(key);
  if (!page) return null;
  return `${webPagePath(page)}#${webEntryAnchorId(key)}`;
}

/** Previous / next theme page in theme order. */
export function webPageNeighbours(
  cat: DocsCatalogue,
  slug: string
): { prev: WebPage | null; next: WebPage | null } {
  const page = cat.webPageBySlug.get(slug);
  if (!page) return { prev: null, next: null };
  return {
    prev: cat.webPages[page.position - 1] ?? null,
    next: cat.webPages[page.position + 1] ?? null,
  };
}

/**
 * Where a link to this entry goes: a WEB entry's section on its theme page
 * (`/docs/web/<slug>#sloj-<key>`), a KingCAD layer's section on its folder page
 * (`…/docs/cad/…#sloj-<xml_id>`); every other entry — a product only KingCAD
 * serves, or a CAD layer the tree does not place — its own page.
 */
export function linkFor(cat: DocsCatalogue, key: string): string {
  const web = webEntryHref(cat, key);
  if (web) return web;
  const place = cat.cadIndex.get(key);
  if (place?.pageId && place.anchor) {
    const page = cat.cadPageById.get(place.pageId);
    if (page) return `${cadPagePath(page)}#${place.anchor}`;
  }
  return entryPath(key);
}

// ---------------------------------------------------------------------------
// KingCAD folder pages + tree nav (0584; folder pages 2026-09-16)
// ---------------------------------------------------------------------------

/** The page at `…/docs/cad/<path…>`, or null. */
export function cadPageForPath(cat: DocsCatalogue, path: readonly string[]): CadPage | null {
  return cat.cadPageByPath.get(path.join('/')) ?? null;
}

/** `…/docs/cad/<slugs…>` of a page. */
export function cadPagePath(page: CadPage): string {
  return cadFolderPath(page.slugs);
}

/** The folder page + anchor of a KingCAD LAYER entry, or null when the tree does not place it. */
export function cadLayerHref(cat: DocsCatalogue, key: string): string | null {
  const place = cat.cadIndex.get(key);
  if (!place?.pageId) return null;
  return linkFor(cat, key);
}

/** Previous / next folder page in tree order. */
export function cadPageNeighbours(
  cat: DocsCatalogue,
  pageId: string
): { prev: CadPage | null; next: CadPage | null } {
  const page = cat.cadPageById.get(pageId);
  if (!page) return { prev: null, next: null };
  return {
    prev: cat.cadPages[page.position - 1] ?? null,
    next: cat.cadPages[page.position + 1] ?? null,
  };
}

// ---------------------------------------------------------------------------
// Section landings as card grids + comma-split folder labels (Teo 2026-09-28)
// ---------------------------------------------------------------------------

export interface FolderLabel {
  /** The name before the publisher seam — »KN - Kataster nepremičnin - parcele«. */
  label: string;
  /** The publisher after it — »Geodetska uprava RS« — a muted second line; null when the name carries none. */
  sub: string | null;
}

/** Words of a string, lowercased, letters and digits only. */
function wordsOf(s: string): string[] {
  return s
    .toLocaleLowerCase('sl')
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w !== '');
}

/**
 * A KingCAD folder name split into the label and the publisher (Teo 2026-09-28: "split at the
 * comma"). The desktop names its include folders »<what>, <publisher>« — but a comma is not
 * always that seam: »Prostorske enote, naslovi, Geodetska uprava RS«, »RABA, GERK - Ministrstvo
 * za kmetijstvo, gozdarstvo in prehrano«, »Šport, rekreacija in prosti čas« (no publisher at
 * all). So the seam is a », « or » - « outside parentheses after which at least half the words
 * belong to the folder's publisher (`publishers`: its map tag spelling plus the wiki source's
 * name and short, `cadFolderLabel`) — the best-scoring one, the earliest on a tie. No publisher
 * known, or no seam matching one → the whole name is the label.
 */
export function splitFolderLabel(
  name: string,
  publishers: readonly (string | null | undefined)[] = []
): FolderLabel {
  const trimmed = name.trim();
  const pub = new Set(publishers.flatMap((p) => (p ? wordsOf(p) : [])));
  if (pub.size === 0) return { label: trimmed, sub: null };
  let best: { at: number; len: number; score: number } | null = null;
  let depth = 0;
  for (let i = 0; i < trimmed.length; i += 1) {
    const ch = trimmed[i];
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (depth > 0) continue;
    let len = 0;
    if (ch === ',') len = trimmed[i + 1] === ' ' ? 2 : 1;
    else if (trimmed.startsWith(' - ', i)) len = 3;
    if (len === 0) continue;
    const rest = wordsOf(trimmed.slice(i + len));
    if (rest.length === 0) continue;
    const score = rest.filter((w) => pub.has(w)).length / rest.length;
    if (score >= 0.5 && (!best || score > best.score)) best = { at: i, len, score };
  }
  if (!best) return { label: trimmed, sub: null };
  const label = trimmed.slice(0, best.at).trim();
  const sub = trimmed.slice(best.at + best.len).trim();
  return label === '' || sub === '' ? { label: trimmed, sub: null } : { label, sub };
}

/** A tree folder's split label: its name against its own publisher spellings (the map tag, the wiki source's name and short). */
export function cadFolderLabel(cat: DocsCatalogue, folder: WikiCadFolderNode): FolderLabel {
  const source = folder.sourceId ? cat.sourceById.get(folder.sourceId) : undefined;
  return splitFolderLabel(folder.name, [folder.sourceName, source?.name, source?.short]);
}

/** The split label of a folder id of the tree (the »Rastri in LiDAR« folder included); the id itself when unknown. */
function cadFolderLabelById(cat: DocsCatalogue, folderId: string): string {
  const folder = cat.cadFolderById.get(folderId);
  return folder ? cadFolderLabel(cat, folder).label : folderId;
}

/** The first few names of a card, » · «-joined, with an ellipsis when more follow. */
export function cardTeaser(names: readonly string[], max = 3): string {
  const shown = names.slice(0, max).join(' · ');
  return names.length > max ? `${shown} …` : shown;
}

/** One card of a section landing (renderCards.ts). */
export interface LandingCard {
  href: string;
  /** The folders above a KingCAD page (» › «-joined labels); null on a web theme or a depth-0 page. */
  eyebrow: string | null;
  title: string;
  /** The publisher half of a comma-split folder name; null otherwise. */
  sub: string | null;
  /** The layer count line (»12 slojev«), or a product's publisher. */
  meta: string;
  /** The first layer names. */
  teaser: string;
}

/** The Web landing: one card per theme page in theme order. */
export function webLandingCards(cat: DocsCatalogue): LandingCard[] {
  return cat.webPages.map((page) => ({
    href: webPagePath(page),
    eyebrow: null,
    title: page.theme,
    sub: null,
    meta: docsCopy.count(page.entries.length),
    teaser: cardTeaser(page.entries.map(navLabel)),
  }));
}

/**
 * The CAD landing: one card per folder page in tree order — the folders above it as the
 * eyebrow (labels only), the folder's split name, its layer count and first layers — then
 * one card per »Rastri in LiDAR« product (its own page, or its theme page section when the
 * web serves it too).
 */
export function cadLandingCards(cat: DocsCatalogue): LandingCard[] {
  const pages = cat.cadPages.map((page): LandingCard => {
    const { label, sub } = cadFolderLabel(cat, page.folder);
    const above = page.folderIds.slice(0, -1).map((id) => cadFolderLabelById(cat, id));
    return {
      href: cadPagePath(page),
      eyebrow: above.length > 0 ? above.join(' › ') : null,
      title: label,
      sub,
      meta: docsCopy.count(page.layers.length),
      teaser: cardTeaser(page.layers.map((l) => l.name)),
    };
  });
  const rastri = cat.rastri.map(
    (e): LandingCard => ({
      href: linkFor(cat, e.key),
      eyebrow: docsCopy.cad.rastriFolder,
      title: navLabel(e),
      sub: null,
      meta: e.source.short,
      teaser: e.title === navLabel(e) ? '' : e.title,
    })
  );
  return [...pages, ...rastri];
}

/**
 * One row of a page-list nav — the KingCAD tree and, since 2026-09-28, the flat
 * list of web theme pages share the shape: a `folder` row is a container (a
 * disclosure button, `href` null) or a PAGE (a link with its layer count); a
 * `layer` row is a product row or a search hit linked to its section.
 */
export type TreeNavRow =
  | {
      kind: 'folder';
      id: string;
      depth: number;
      label: string;
      /** The publisher half of a comma-split folder name (Teo 2026-09-28), a muted second line; null when none. */
      sub: string | null;
      open: boolean;
      /** Layer rows beneath (all depths). */
      count: number;
      /** The active page or entry sits somewhere under this folder. */
      onPath: boolean;
      /** The page this row links to; null for a container folder (a disclosure button). */
      href: string | null;
      /** This row's page is the one shown. */
      active: boolean;
    }
  | {
      kind: 'layer';
      /** The entry documenting the layer; null when the seed has none. */
      key: string | null;
      xmlId: string;
      depth: number;
      label: string;
      title: string;
      active: boolean;
      /** A »Rastri in LiDAR« row: the product's page; a search hit: the layer's section on its page. */
      href: string;
      /** The page a search hit sits on (its name); null for a product row. */
      folderName: string | null;
    };

/** @deprecated the KingCAD name of `TreeNavRow`; both navs share the shape now. */
export type CadNavRow = TreeNavRow;

/** What the nav marks as current: a folder page, or a »Rastri in LiDAR« product entry. */
export interface CadNavActive {
  pageId: string | null;
  key: string | null;
}

/** Layer rows beneath each folder id (all depths). */
function countLayers(rows: readonly WikiCadNode[]): Map<string, number> {
  const counts = new Map<string, number>();
  const stack: string[] = [];
  for (const n of rows) {
    if (n.kind === 'folder') {
      stack.length = n.depth;
      stack.push(n.id);
      counts.set(n.id, 0);
      continue;
    }
    for (const id of stack.slice(0, n.depth)) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

/**
 * The web page-list nav (2026-09-28), the CAD tree's flat twin: one link row per
 * theme page with its entry count, current when shown. Searching lists the hits
 * flat: matching pages (on the theme name), then matching entries linked to their
 * section with the theme name alongside.
 */
export function buildWebNav(
  cat: DocsCatalogue,
  query: string,
  activeSlug: string | null
): TreeNavRow[] {
  const out: TreeNavRow[] = [];
  const searching = query.trim() !== '';
  if (searching) {
    const nq = fold(query.trim());
    for (const page of cat.webPages) {
      if (!fold(page.theme).includes(nq)) continue;
      out.push({
        kind: 'folder',
        id: page.slug,
        depth: 0,
        label: page.theme,
        sub: null,
        open: true,
        count: page.entries.length,
        onPath: false,
        href: webPagePath(page),
        active: page.slug === activeSlug,
      });
    }
    for (const page of cat.webPages) {
      for (const e of page.entries) {
        if (!matchesQuery(e, query)) continue;
        out.push({
          kind: 'layer',
          key: e.key,
          xmlId: '',
          depth: 0,
          label: navLabel(e),
          title: e.title,
          active: false,
          href: `${webPagePath(page)}#${webEntryAnchorId(e.key)}`,
          folderName: page.theme,
        });
      }
    }
    return out;
  }
  for (const page of cat.webPages) {
    out.push({
      kind: 'folder',
      id: page.slug,
      depth: 0,
      label: page.theme,
      sub: null,
      open: true,
      count: page.entries.length,
      onPath: false,
      href: webPagePath(page),
      active: page.slug === activeSlug,
    });
  }
  return out;
}

/**
 * The collapsible KingCAD nav: container folders are disclosure buttons, closed
 * by default (a node shows only when every ancestor is open); folder PAGES are
 * links and count as open, so a page nested under a page is always in view.
 * Layers are not rows — their page lists them — except the »Rastri in LiDAR«
 * products. Searching drops the tree and lists the hits flat: matching pages,
 * then matching layers (linked to their section, folder name alongside).
 */
export function buildCadNav(
  cat: DocsCatalogue,
  query: string,
  active: CadNavActive,
  open: ReadonlySet<string>
): TreeNavRow[] {
  const rows = cadFlatRows(cat);
  const out: TreeNavRow[] = [];
  const searching = query.trim() !== '';
  if (searching) {
    const nq = fold(query.trim());
    for (const page of cat.cadPages) {
      if (!fold(page.folder.name).includes(nq)) continue;
      const { label, sub } = cadFolderLabel(cat, page.folder);
      out.push({
        kind: 'folder',
        id: page.id,
        depth: 0,
        label,
        sub,
        open: true,
        count: page.layers.length,
        onPath: false,
        href: cadPagePath(page),
        active: page.id === active.pageId,
      });
    }
    const stack: WikiCadFolderNode[] = [];
    for (const n of rows) {
      if (n.kind === 'folder') {
        stack.length = n.depth;
        stack.push(n);
        continue;
      }
      const e = n.entryKey ? cat.byKey.get(n.entryKey) : undefined;
      const hit = e ? matchesQuery(e, query) : fold(n.name).includes(nq);
      if (!hit) continue;
      const folder = stack[n.depth - 1];
      const page = folder ? cat.cadPageById.get(folder.id) : undefined;
      if (page) {
        out.push({
          kind: 'layer',
          key: e?.key ?? null,
          xmlId: n.xmlId,
          depth: 0,
          label: e ? navLabel(e) : n.name,
          title: e?.title ?? n.name,
          active: false,
          href: `${cadPagePath(page)}#${cadLayerAnchorId(n.xmlId)}`,
          folderName: cadFolderLabel(cat, page.folder).label,
        });
      } else if (e) {
        // A »Rastri in LiDAR« product: its theme page section when the web serves it, else its own page.
        out.push({
          kind: 'layer',
          key: e.key,
          xmlId: n.xmlId,
          depth: 0,
          label: navLabel(e),
          title: e.title,
          active: e.key === active.key,
          href: linkFor(cat, e.key),
          folderName: null,
        });
      }
    }
    return out;
  }
  const counts = countLayers(rows);
  const activePage = active.pageId ? cat.cadPageById.get(active.pageId) : undefined;
  const activeFolders = new Set<string>([
    ...(activePage ? activePage.folderIds.slice(0, -1) : []),
    ...(active.key ? (cat.cadIndex.get(active.key)?.folderIds ?? []) : []),
  ]);
  const isOpen = (id: string): boolean => open.has(id) || cat.cadPageById.has(id);
  const stack: string[] = [];
  for (const n of rows) {
    if (n.kind === 'folder') {
      stack.length = n.depth;
      stack.push(n.id);
      // visible when every ancestor is open
      if (!stack.slice(0, n.depth).every(isOpen)) continue;
      const page = cat.cadPageById.get(n.id) ?? null;
      const { label, sub } = cadFolderLabel(cat, n);
      out.push({
        kind: 'folder',
        id: n.id,
        depth: n.depth,
        label,
        sub,
        open: isOpen(n.id),
        count: counts.get(n.id) ?? 0,
        onPath: activeFolders.has(n.id),
        href: page ? cadPagePath(page) : null,
        active: !!page && page.id === active.pageId,
      });
      continue;
    }
    if (!stack.slice(0, n.depth).every(isOpen)) continue;
    // Only the »Rastri in LiDAR« products are layer rows in the tree; folder pages list their layers.
    if (stack[n.depth - 1] !== RASTRI_FOLDER_ID) continue;
    const e = n.entryKey ? cat.byKey.get(n.entryKey) : undefined;
    if (!e) continue;
    out.push({
      kind: 'layer',
      key: e.key,
      xmlId: n.xmlId,
      depth: n.depth,
      label: navLabel(e),
      title: e.title,
      active: e.key === active.key,
      href: linkFor(cat, e.key),
      folderName: null,
    });
  }
  return out;
}

/** Hits while searching: the pages and layers listed. */
export function navHitCount(rows: readonly TreeNavRow[]): number {
  return rows.reduce((n, r) => n + (r.kind === 'layer' || r.href !== null ? 1 : 0), 0);
}

/** @deprecated the KingCAD name of `navHitCount`. */
export const cadVisibleCount = navHitCount;

/**
 * Previous / next »Rastri in LiDAR« product — the only entry pages the tree lists
 * (a KingCAD layer reads on its folder page, whose pager walks the pages).
 */
export function cadNeighbours(
  cat: DocsCatalogue,
  key: string
): { prev: WikiEntry | null; next: WikiEntry | null } {
  const idx = cat.rastri.findIndex((e) => e.key === key);
  if (idx < 0) return { prev: null, next: null };
  return { prev: cat.rastri[idx - 1] ?? null, next: cat.rastri[idx + 1] ?? null };
}
